import classNames from 'classnames';
import {
 addDays, addHours, addYears, format, fromUnixTime, isBefore, isToday, isTomorrow,
} from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Flatpickr from 'react-flatpickr';
import confirmDatePlugin from 'flatpickr/dist/plugins/confirmDate/confirmDate';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Panel from './Panel';
import SaveButtons from './SaveButtons';
import { resolveRequestScheduledTime } from './schema';

const MAX_DEADLINE_DATE = new Date('2099-12-31T23:59');

const BASE_FLATPICKR_OPTIONS = {
  enableTime: true,
  disableMobile: true,
  minuteIncrement: 1,
  // eslint-disable-next-line new-cap
  plugins: [new confirmDatePlugin({})],
};

const FLATPICKR_DATE_FORMAT = "yyyy-MM-dd'T'HH:mm";

function formatCalendar(date) {
  const time = format(date, 'h:mm a');
  let day;

  if (isToday(date)) day = 'Today';
  else if (isTomorrow(date)) day = 'Tomorrow';
  else if (isBefore(date, addDays(new Date(), 7))) day = format(date, 'EEEE');
  else if (isBefore(date, addYears(new Date(), 1))) day = format(date, 'EEEE, MMM do');
  else day = format(date, 'EEEE, MMM do, yyyy');

  return (
    <>
      {day}
      <br />
      at {time}
    </>
  );
}

function formatScheduleLabel(date) {
  // Format schedule label for display: "Feb 15, 10:00 AM"
  return format(date, 'MMM d, h:mm a');
}

function Option({
 checked, children, description, name, onChange, tooltip = null, value,
}) {
  const id = `${name}_${value}`;
  return (
    <div className="col mb-4 align-self-stretch">
      <label
        htmlFor={id}
        className={classNames('card d-flex flex-row h-100 mw-100 flex-shrink-0 flex-grow-1', {
          'border-primary': checked,
        })}
        style={{ padding: '14px' }}
        aria-label={description}
      >
        <div>
          <input
            id={id}
            checked={checked}
            className="me-2 form-check-input"
            name={name}
            onChange={onChange}
            type="radio"
            value={value}
          />
        </div>
        <div>
          <h3 className="mb-1 fw-semibold" style={{ fontSize: '16px' }}>
            {description}
            {tooltip !== null && (
              <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
                <i className="ms-1 text-muted fa fa-info-circle" />
              </OverlayTrigger>
            )}
          </h3>
          <span className="text-secondary" style={{ fontSize: '16px' }}>
            {children}
          </span>
        </div>
      </label>
    </div>
  );
}

Option.propTypes = {
  checked: PropTypes.bool.isRequired,
  children: PropTypes.node,
  description: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  tooltip: PropTypes.string,
  value: PropTypes.string.isRequired,
};

Option.defaultProps = {
  children: null,
  tooltip: null,
};

function ScheduleOption({
 value, checked, description, onChange,
}) {
  const id = `schedule_option_${value}`;
  return (
    <label
      className="mb-1 d-flex"
      style={{ width: 'fit-content' }}
      htmlFor={id}
      aria-label={`Schedule for ${description}`}
    >
      <input
        id={id}
        checked={checked}
        className="me-2 form-check-input"
        name="scheduled"
        onChange={onChange}
        type="radio"
        value={value}
      />
      <span style={{ fontSize: '16px' }}>{description}</span>
    </label>
  );
}

ScheduleOption.propTypes = {
  value: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  description: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

function Deadline({
  abandonChangesAndExit,
  customDeadlineDateTime,
  customScheduleDateTime,
  deadline,
  markFieldTouched,
  postRequest,
  saveDraft,
  saveAndExit,
  schedule,
  savingState,
  requestStatus,
  timeNow,
  updateParam,
  useUpdateParamWithEvent,
  validationErrors,
}) {
  const canChangeSchedule = requestStatus !== 'posted';
  const scheduleChangedHandler = useUpdateParamWithEvent('schedule');

  const handleScheduleDateChange = useCallback((selectedDates) => {
    if (selectedDates && selectedDates.length > 0) {
      updateParam('customScheduleDateTime', format(selectedDates[0], FLATPICKR_DATE_FORMAT));
    }
    // Mark field as touched when Flatpickr closes (similar to onBlur)
    markFieldTouched('customScheduleDateTime');
  }, [updateParam, markFieldTouched]);

  const handleDeadlineDateChange = useCallback((selectedDates) => {
    if (selectedDates && selectedDates.length > 0) {
      updateParam('customDeadlineDateTime', format(selectedDates[0], FLATPICKR_DATE_FORMAT));
    }
    // Mark field as touched when Flatpickr closes (similar to onBlur)
    markFieldTouched('customDeadlineDateTime');
  }, [updateParam, markFieldTouched]);

  // Bucket timeNow to 60s so Flatpickr's minDate doesn't change every second.
  // Per-second minDate changes cause react-flatpickr to call set('minDate', ...),
  // which jumps the visible calendar back to minDate's month while the user is
  // trying to navigate forward.
  // Round UP to the next minute to ensure minDate never lags behind the real timeNow,
  // preventing validation errors when users select the earliest allowed minute.
  const minDateTick = Math.ceil(timeNow / 60);
  const baseDateTime = useMemo(
    () => resolveRequestScheduledTime({
      customScheduleDateTime, schedule, timeNow: minDateTick * 60,
    }),
    [customScheduleDateTime, schedule, minDateTick],
  );
  const dateTimeNow = useMemo(() => fromUnixTime(minDateTick * 60), [minDateTick]);

  const scheduleFlatpickrOptions = useMemo(
    () => ({ ...BASE_FLATPICKR_OPTIONS, minDate: dateTimeNow, maxDate: MAX_DEADLINE_DATE }),
    [dateTimeNow],
  );
  const deadlineFlatpickrOptions = useMemo(
    () => ({ ...BASE_FLATPICKR_OPTIONS, minDate: baseDateTime, maxDate: MAX_DEADLINE_DATE }),
    [baseDateTime],
  );

  const [scheduleShowing, setScheduleShowing] = useState(false);
  const scheduleShowingClickedHandler = useCallback(() => {
    if (canChangeSchedule) {
      setScheduleShowing(!scheduleShowing);
    }
  }, [canChangeSchedule, scheduleShowing]);

  const schedulingCustomDateTime = scheduleShowing && schedule === 'custom';

  // Determine schedule label text
  let scheduleLabel = 'Now';
  if (schedule === 'custom') {
    if (customScheduleDateTime) {
      const customDate = baseDateTime;
      const formattedDate = formatScheduleLabel(customDate);
      // Truncate if too long (more than 20 characters)
      if (formattedDate.length > 20) {
        scheduleLabel = `${formattedDate.substring(0, 17)}...`;
      } else {
        scheduleLabel = formattedDate;
      }
    } else {
      // Hide label when custom is selected but no date entered
      scheduleLabel = null;
    }
  }

  return (
    <div>
      <Panel>
        <div className="mb-1 paragraph-md">
          <b>Schedule:</b>{' '}
          {/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
          <span
            role={canChangeSchedule ? 'button' : undefined}
            tabIndex={canChangeSchedule ? 0 : undefined}
            style={{ borderBottom: 'dashed 1px' }}
            onClick={scheduleShowingClickedHandler}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                scheduleShowingClickedHandler();
              }
            }}
          >
            {scheduleLabel && <>{scheduleLabel} </>}
            {(canChangeSchedule && (
              <i
                className={classNames('fa', { 'fa-chevron-up': scheduleShowing, 'fa-chevron-down': !scheduleShowing })}
              />
            )) || <i>(request is posted)</i>}
          </span>
          {/* eslint-enable jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        </div>

        {scheduleShowing && (
          <>
            <ScheduleOption
              description="Now"
              value="now"
              checked={schedule === 'now'}
              onChange={scheduleChangedHandler}
            />
            <ScheduleOption
              description="Custom post date"
              value="custom"
              checked={schedule === 'custom'}
              onChange={scheduleChangedHandler}
            />
          </>
        )}

        {schedulingCustomDateTime && (
          <>
            <div
              className={classNames('mt-2 mb-0 input-group', {
                'is-invalid': !!validationErrors.customScheduleDateTime,
              })}
            >
              <Flatpickr
                className={classNames('form-control', {
                  'is-invalid': !!validationErrors.customScheduleDateTime,
                })}
                placeholder="When do you want to post it?"
                options={scheduleFlatpickrOptions}
                value={customScheduleDateTime || undefined}
                onClose={handleScheduleDateChange}
              />
              <span className="input-group-text">
                <i className="fa-light fa-clock" />
              </span>
            </div>
            {validationErrors.customScheduleDateTime && (
              <div className="invalid-feedback d-block">{validationErrors.customScheduleDateTime}</div>
            )}
          </>
        )}

        <div className="mt-4 mb-2" style={{ fontSize: '16px' }}>
          <b>When will you stop accepting pitches?</b>
        </div>
        <div>
          <div className="row" style={{ marginBottom: '-1.5rem' }}>
            <Option
              description="In 24 hours"
              name="deadline"
              value="24hrs"
              checked={deadline === '24hrs'}
              onChange={useUpdateParamWithEvent('deadline')}
            >
              {formatCalendar(addHours(baseDateTime, 24))}
            </Option>
            <Option
              description="In 72 hours"
              name="deadline"
              value="72hrs"
              checked={deadline === '72hrs'}
              onChange={useUpdateParamWithEvent('deadline')}
            >
              {formatCalendar(addHours(baseDateTime, 72))}
            </Option>
            <Option
              description="In 7 days"
              name="deadline"
              value="7days"
              checked={deadline === '7days'}
              onChange={useUpdateParamWithEvent('deadline')}
            >
              {formatCalendar(addDays(baseDateTime, 7))}
            </Option>
            <Option
              description="Custom"
              name="deadline"
              value="custom"
              checked={deadline === 'custom'}
              onChange={useUpdateParamWithEvent('deadline')}
            >
              <div>Choose specific date/time</div>
              {deadline === 'custom' && (
                <>
                  <div
                    className={classNames('mt-1 mb-0 input-group', {
                      'is-invalid': !!validationErrors.customDeadlineDateTime,
                    })}
                  >
                    <Flatpickr
                      className={classNames('form-control', {
                        'is-invalid': !!validationErrors.customDeadlineDateTime,
                      })}
                      placeholder="Choose a date and time"
                      options={deadlineFlatpickrOptions}
                      value={customDeadlineDateTime || undefined}
                      onClose={handleDeadlineDateChange}
                    />
                    <span className="input-group-text">
                      <i className="fa-light fa-clock" />
                    </span>
                  </div>
                  {validationErrors.customDeadlineDateTime && (
                    <div className="invalid-feedback d-block">{validationErrors.customDeadlineDateTime}</div>
                  )}
                </>
              )}
            </Option>
          </div>
        </div>
      </Panel>

      <div className="my-4 px-3 d-flex justify-content-end">
        <SaveButtons
          abandonChangesAndExit={abandonChangesAndExit}
          hasCustomSchedule={schedule === 'custom'}
          postRequest={postRequest}
          saveDraft={saveDraft}
          saveAndExit={saveAndExit}
          savingState={savingState}
          requestStatus={requestStatus}
        />
      </div>
    </div>
  );
}

Deadline.propTypes = {
  abandonChangesAndExit: PropTypes.func,
  customDeadlineDateTime: PropTypes.string,
  customScheduleDateTime: PropTypes.string,
  deadline: PropTypes.string,
  markFieldTouched: PropTypes.func.isRequired,
  postRequest: PropTypes.func.isRequired,
  requestStatus: PropTypes.oneOf(['draft', 'scheduled', 'posted']),
  saveDraft: PropTypes.func.isRequired,
  saveAndExit: PropTypes.func,
  savingState: PropTypes.string.isRequired,
  schedule: PropTypes.string,
  timeNow: PropTypes.number.isRequired,
  updateParam: PropTypes.func.isRequired,
  useUpdateParamWithEvent: PropTypes.func.isRequired,
  validationErrors: PropTypes.shape({
    customScheduleDateTime: PropTypes.string,
    customDeadlineDateTime: PropTypes.string,
  }),
  // These props are passed through to SaveButtons
  // eslint-disable-next-line react/no-unused-prop-types
  title: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types
  details: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types
  mediaOutletId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

Deadline.defaultProps = {
  abandonChangesAndExit: null,
  customDeadlineDateTime: '',
  customScheduleDateTime: '',
  deadline: '7days',
  requestStatus: null,
  saveAndExit: null,
  schedule: 'now',
  validationErrors: {},
  title: '',
  details: '',
  mediaOutletId: null,
};

export default Deadline;
